import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import api from '../../api/client';
import DurationDisplay from '../common/DurationDisplay';

const CAMPAIGN_FILTERS = [
  { value: 'all', label: 'Todas las campañas' },
];

export default function AdHistory() {
  const [history, setHistory] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [spotFilter, setSpotFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchCampaigns();
    fetchHistory();
  }, [campaignFilter, spotFilter, dateFrom, dateTo, page]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get('/api/campaigns');
      setCampaigns(data);
      const spots = data.flatMap((c) =>
        (c.spots || []).map((s) => ({ value: s.id, label: s.title, campaign: c.name }))
      );
      setSpots(spots);
    } catch (err) {
      console.error('[AdHistory] Failed to fetch campaigns:', err);
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (campaignFilter && campaignFilter !== 'all') params.campaign_id = campaignFilter;
      if (spotFilter) params.spot_id = spotFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const { data } = await api.get('/api/history/ads', { params });
      setHistory(data.history || data);
    } catch (err) {
      console.error('[AdHistory] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const [spots, setSpots] = useState([]);

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando historial de publicidad...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Historial de Emisiones</h1>
        <button onClick={fetchHistory} className="btn btn-secondary btn-sm" title="Actualizar">
          ↻
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <select
          value={campaignFilter}
          onChange={(e) => setCampaignFilter(e.target.value)}
          className="input text-sm w-48"
        >
          <option value="all">Todas las campañas</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          value={spotFilter}
          onChange={(e) => setSpotFilter(e.target.value)}
          className="input text-sm w-48"
        >
          <option value="">Todos los spots</option>
          {spots.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label} ({s.campaign})
            </option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="input text-sm w-40"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="input text-sm w-40"
        />
      </div>

      {history.length === 0 ? (
        <div className="py-12 text-center text-dark-400">No hay historial de publicidad.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Emitido el</th>
                <th>Spot</th>
                <th>Campaña</th>
                <th>Anunciante</th>
                <th>Duración</th>
                <th>Reproducciones</th>
              </tr>
            </thead>
            <tbody>
              {history.map((entry) => (
                <tr key={entry.id}>
                  <td className="text-sm text-dark-400">
                    {entry.broadcast_at ? format(parseISO(entry.broadcast_at), 'PPp') : '—'}
                  </td>
                  <td className="font-medium text-dark-100">{entry.spot_title || '—'}</td>
                  <td className="text-dark-300">{entry.campaign_name || '—'}</td>
                  <td className="text-dark-300">{entry.advertiser_name || '—'}</td>
                  <td className="text-sm text-dark-400">
                    {entry.duration_seconds && <DurationDisplay seconds={entry.duration_seconds} />}
                  </td>
                  <td className="text-dark-300">{entry.play_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {history.length > 0 && (
        <div className="flex justify-between text-sm text-dark-400">
          <span>Página {page}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1 || loading}
              className="btn btn-secondary btn-sm"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={loading}
              className="btn btn-secondary btn-sm"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
